/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ActionKernelType,
  ActionType,
  StationPolicy,
  StreamingDeviceType,
  StreamingRestrictionsInternal
} from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../device";
import { PlaybackContentType } from "../enums";
import { PlaybackRestrictionType } from "../enums";
import type { IMetadata, IPlayable, IRestriction } from "../interfaces";
import type { TMetadataRestrictionParams } from "../types";

/**
 * Interim type for action kernel used in NDVR device restriction checks.
 * TODO: Replace with proper type from client-code/actions when Phase 0-A.1 is complete.
 */
type ActionKernel = {
  actionKernelType?: ActionKernelType;
  isRestrictedByDeviceType?: boolean;
};

/**
 * Interim type for watch action used in NDVR device restriction checks.
 * TODO: Replace with proper type from client-code/actions when Phase 0-A.1 is complete.
 */
type WatchAction = {
  actionType?: ActionType;
  kernel?: ActionKernel;
};

/**
 * Extended channel type that includes policy information.
 * Used for device restriction checks.
 */
interface ChannelWithPolicy {
  policyInfo?: Array<StationPolicy>;
}

export class MetadataRestrictionImpl implements IRestriction<TMetadataRestrictionParams> {
  private checkDeviceTypeRestriction(
    policyInfo: Array<StationPolicy | undefined>,
    playbackContentType: PlaybackContentType,
    metadata: IMetadata | undefined
  ): boolean {
    if (
      playbackContentType === PlaybackContentType.NDVR_LIVE ||
      playbackContentType === PlaybackContentType.NDVR_RECORDED
    ) {
      return this.checkDeviceTypeRestrictionForNDVR(metadata);
    }

    if (policyInfo && !policyInfo.length) return false;

    if (playbackContentType === PlaybackContentType.LIVE) {
      return this.checkDeviceTypeRestrictionForLive(
        policyInfo,
        playbackContentType
      );
    }
    return this.checkDeviceTypeRestrictionForSoCu(policyInfo);
  }

  private checkDeviceTypeRestrictionForLive(
    policyInfo: Array<StationPolicy | undefined>,
    playbackContentType: PlaybackContentType
  ): boolean {
    const deviceType = <StreamingDeviceType>(
      DeviceModel.getTiVoSettings().getDeviceType()
    );

    const filteredPolicies: StreamingRestrictionsInternal[] =
      this.getFilteredStationPolicies(policyInfo, playbackContentType);

    for (let _index = 0; _index < filteredPolicies.length; _index += 1) {
      const streamingRestrictions = filteredPolicies[_index];

      if (
        streamingRestrictions &&
        streamingRestrictions.restrictedDeviceType &&
        streamingRestrictions.restrictedDeviceType.includes(deviceType) &&
        !streamingRestrictions.checkDeviceTypeWithMso
      )
        return true;
    }

    return false;
  }

  private checkDeviceTypeRestrictionForNDVR(
    metadata: IMetadata | undefined
  ): boolean {
    if (
      metadata !== null &&
      metadata !== undefined &&
      metadata.actions !== null &&
      metadata.actions !== undefined
    ) {
      const actions = metadata.actions as WatchAction[] | undefined;
      if (actions === null || actions === undefined) {
        return false;
      }
      const watchAction = actions.find((action: WatchAction) => {
        return (
          action.actionType === ActionType.WatchNowAction &&
          action.kernel !== null &&
          action.kernel !== undefined &&
          action.kernel.actionKernelType ===
            ActionKernelType.WatchFromMyShowsActionKernel
        );
      });
      if (
        watchAction !== null &&
        watchAction !== undefined &&
        watchAction.kernel !== null &&
        watchAction.kernel !== undefined
      ) {
        return !!watchAction.kernel.isRestrictedByDeviceType;
      }
    }
    return false;
  }

  private checkDeviceTypeRestrictionForSoCu(
    policyInfo: Array<StationPolicy | undefined>
  ): boolean {
    if (policyInfo && policyInfo.length > 0) {
      for (let i = 0; i < policyInfo.length; i += 1) {
        const policy = policyInfo[i];
        const streamingRules =
          policy && policy.linearStationRules
            ? policy.linearStationRules.streamingRules
            : undefined;
        const soStreamingRestrictions = streamingRules
          ? streamingRules.soStreamingRestrictions
          : undefined;
        const cuStreamingRestrictions = streamingRules
          ? streamingRules.cuStreamingRestrictions
          : undefined;
        if (
          this.isDeviceRestricted(soStreamingRestrictions) &&
          this.isDeviceRestricted(cuStreamingRestrictions)
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private checkDeviceTypeRestrictionForVOD(
    metadata: IMetadata | undefined
  ): boolean {
    return !!(
      metadata &&
      metadata.watchVodAction &&
      metadata.watchVodAction.kernel &&
      metadata.watchVodAction.kernel.isStreamingRestrictedByDeviceType
    );
  }

  private isDeviceRestricted(
    streamingRestrictions: StreamingRestrictionsInternal | undefined
  ): boolean {
    if (!streamingRestrictions || !streamingRestrictions.restrictedDeviceType) {
      return false;
    }
    return !!(
      streamingRestrictions.restrictedDeviceType.includes(
        <StreamingDeviceType>DeviceModel.getTiVoSettings().getDeviceType()
      ) && !streamingRestrictions.checkDeviceTypeWithMso
    );
  }

  // TODO: move to utils
  private getFilteredStationPolicies(
    policyInfo: Array<StationPolicy | undefined>,
    playbackContentType: PlaybackContentType
  ): StreamingRestrictionsInternal[] {
    let filteredPolicies: StreamingRestrictionsInternal[] = [];
    if (playbackContentType) {
      filteredPolicies = policyInfo.reduce<StreamingRestrictionsInternal[]>(
        (acc, policy) => {
          if (
            policy !== null &&
            policy !== undefined &&
            policy.linearStationRules !== null &&
            policy.linearStationRules !== undefined &&
            policy.linearStationRules.streamingRules !== null &&
            policy.linearStationRules.streamingRules !== undefined &&
            policy.linearStationRules.streamingRules
              .linearStreamingRestrictions !== null &&
            policy.linearStationRules.streamingRules
              .linearStreamingRestrictions !== undefined
          ) {
            acc.push(
              policy.linearStationRules.streamingRules
                .linearStreamingRestrictions
            );
          }
          return acc;
        },
        []
      );
    }
    return filteredPolicies;
  }

  public async checkRestriction(data: {
    playable: IPlayable;
    metadata?: IMetadata;
  }): Promise<PlaybackRestrictionType> {
    const { playable, metadata } = data;
    const { channel, playbackContentType } = playable;

    // VOD is decided by its watch action alone. It is not channel-bound, so
    // the station-policy and subscription checks below do not describe it,
    // even when a caller attaches a channel for display purposes.
    if (playbackContentType === PlaybackContentType.VOD) {
      return this.checkDeviceTypeRestrictionForVOD(metadata)
        ? PlaybackRestrictionType.DEVICE_RESTRICTED
        : PlaybackRestrictionType.NO_RESTRICTION;
    }

    const channelWithPolicy = channel as ChannelWithPolicy | undefined;
    if (
      channelWithPolicy &&
      channelWithPolicy.policyInfo &&
      this.checkDeviceTypeRestriction(
        channelWithPolicy.policyInfo,
        playbackContentType,
        metadata
      )
    )
      return PlaybackRestrictionType.DEVICE_RESTRICTED;

    if (
      (playbackContentType === PlaybackContentType.NDVR_LIVE ||
        playbackContentType === PlaybackContentType.NDVR_RECORDED) &&
      this.checkDeviceTypeRestrictionForNDVR(metadata)
    )
      return PlaybackRestrictionType.DEVICE_RESTRICTED;

    if (channel && !channel.isSubscribed)
      return PlaybackRestrictionType.UNSUBSCRIBED;

    return PlaybackRestrictionType.NO_RESTRICTION;
  }
}
